import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Divider,
  LinearProgress,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  TrendingFlat,
  Assessment,
  Insights,
  MonitorHeart,
  Warning,
  CheckCircle,
  Timeline,
  BarChart,
  ShowChart,
  Favorite,
  AccessTime,
  CalendarToday,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart as RechartsBarChart,
  Bar,
  Area,
  AreaChart,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

const AnalyticsPage = () => {
  const { token } = useSelector(state => state.auth);
  const [analytics, setAnalytics] = useState(null);
  const [readings, setReadings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [period, setPeriod] = useState('month');

  // Fetch analytics data
  const fetchAnalytics = async (selectedPeriod = period) => {
    try {
      setLoading(true);
      
      // Fetch analytics
      const analyticsResponse = await fetch(`/api/blood-pressure/analytics?period=${selectedPeriod}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (!analyticsResponse.ok) {
        throw new Error('Failed to fetch analytics');
      }
      
      const analyticsData = await analyticsResponse.json();
      setAnalytics(analyticsData.analytics);

      // Fetch readings for additional charts
      const readingsResponse = await fetch('/api/blood-pressure', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (!readingsResponse.ok) {
        throw new Error('Failed to fetch readings');
      }
      
      const readingsData = await readingsResponse.json();
      setReadings(readingsData.readings || []);
      
    } catch (err) {
      setError('Failed to load analytics data');
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, [token]);

  const handlePeriodChange = (event) => {
    const newPeriod = event.target.value;
    setPeriod(newPeriod);
    fetchAnalytics(newPeriod);
  };

  const getBloodPressureStatus = (systolic, diastolic) => {
    if (systolic >= 180 || diastolic >= 120) {
      return { status: 'Hypertensive Crisis', color: 'error', severity: 'high' };
    }
    if (systolic >= 140 || diastolic >= 90) {
      return { status: 'High Stage 2', color: 'error', severity: 'high' };
    }
    if (systolic >= 130 || diastolic >= 80) {
      return { status: 'High Stage 1', color: 'warning', severity: 'medium' };
    }
    if (systolic >= 120 && diastolic < 80) {
      return { status: 'Elevated', color: 'warning', severity: 'medium' };
    }
    return { status: 'Normal', color: 'success', severity: 'low' };
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'improving':
      case 'improved':
        return <TrendingDown color="success" />;
      case 'stable':
        return <TrendingFlat color="info" />;
      case 'worsening':
      case 'worsened':
        return <TrendingUp color="error" />;
      default:
        return <TrendingFlat color="info" />;
    }
  };

  const formatChartData = () => {
    return readings.slice(-30).map(reading => ({
      date: new Date(reading.measurementDate).toLocaleDateString(),
      systolic: reading.systolic,
      diastolic: reading.diastolic,
      pulse: reading.pulse,
      timestamp: new Date(reading.measurementDate).getTime(),
    })).sort((a, b) => a.timestamp - b.timestamp);
  };

  const getReadingsByTimeOfDay = () => {
    const timeGroups = {
      morning: [],
      afternoon: [],
      evening: [],
      night: [],
    };

    readings.forEach(reading => {
      const timeOfDay = reading.timeOfDay || 'morning';
      timeGroups[timeOfDay].push(reading);
    });

    return Object.entries(timeGroups).map(([time, readings]) => ({
      time: time.charAt(0).toUpperCase() + time.slice(1),
      count: readings.length,
      avgSystolic: readings.length > 0 ? Math.round(readings.reduce((sum, r) => sum + r.systolic, 0) / readings.length) : 0,
      avgDiastolic: readings.length > 0 ? Math.round(readings.reduce((sum, r) => sum + r.diastolic, 0) / readings.length) : 0,
    }));
  };

  const getReadingsByCategory = () => {
    const categories = {
      Normal: 0,
      Elevated: 0,
      'High Stage 1': 0,
      'High Stage 2': 0,
      'Hypertensive Crisis': 0,
    };

    readings.forEach(reading => {
      const status = getBloodPressureStatus(reading.systolic, reading.diastolic);
      categories[status.status] = (categories[status.status] || 0) + 1;
    });

    return Object.entries(categories).map(([category, count]) => ({
      category,
      count,
      percentage: readings.length > 0 ? Math.round((count / readings.length) * 100) : 0,
    }));
  };

  const COLORS = ['#4caf50', '#ff9800', '#f44336', '#d32f2f', '#b71c1c'];

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box mb={4}>
        <Typography variant="h4" component="h1" gutterBottom>
          Blood Pressure Analytics
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Comprehensive insights into your blood pressure trends and patterns
        </Typography>
      </Box>

      {/* Period Selection */}
      <Box mb={4}>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Analysis Period</InputLabel>
          <Select
            value={period}
            onChange={handlePeriodChange}
            label="Analysis Period"
          >
            <MenuItem value="week">Last Week</MenuItem>
            <MenuItem value="month">Last Month</MenuItem>
            <MenuItem value="quarter">Last Quarter</MenuItem>
            <MenuItem value="year">Last Year</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {!analytics || analytics.totalReadings === 0 ? (
        <Alert severity="info">
          No data available for the selected period. Start recording your blood pressure readings to see analytics.
        </Alert>
      ) : (
        <Grid container spacing={3}>
          {/* Key Metrics */}
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                  <MonitorHeart color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">Total Readings</Typography>
                </Box>
                <Typography variant="h4" color="primary">
                  {analytics.totalReadings}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                  <Assessment color="secondary" sx={{ mr: 1 }} />
                  <Typography variant="h6">Average BP</Typography>
                </Box>
                <Typography variant="h4" color="secondary">
                  {analytics.averages?.systolic || 0}/{analytics.averages?.diastolic || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                  <Favorite color="error" sx={{ mr: 1 }} />
                  <Typography variant="h6">Average Pulse</Typography>
                </Box>
                <Typography variant="h4" color="error">
                  {analytics.averages?.pulse || 0} bpm
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                  {getTrendIcon(analytics.trends?.overall)}
                  <Typography variant="h6">Trend</Typography>
                </Box>
                <Typography variant="h6" color="text.secondary">
                  {analytics.trends?.overall || 'Stable'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Blood Pressure Trends Chart */}
          <Grid item xs={12}>
            <Card>
              <CardHeader 
                title="Blood Pressure Trends"
                avatar={<ShowChart color="primary" />}
              />
              <CardContent>
                <ResponsiveContainer width="100%" height={400}>
                  <LineChart data={formatChartData()}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="systolic" 
                      stroke="#f44336" 
                      name="Systolic" 
                      strokeWidth={2}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="diastolic" 
                      stroke="#2196f3" 
                      name="Diastolic" 
                      strokeWidth={2}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* Readings by Time of Day */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardHeader 
                title="Readings by Time of Day"
                avatar={<AccessTime color="primary" />}
              />
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <RechartsBarChart data={getReadingsByTimeOfDay()}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="time" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="avgSystolic" fill="#f44336" name="Avg Systolic" />
                    <Bar dataKey="avgDiastolic" fill="#2196f3" name="Avg Diastolic" />
                  </RechartsBarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* BP Category Distribution */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardHeader 
                title="Blood Pressure Categories"
                avatar={<BarChart color="primary" />}
              />
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={getReadingsByCategory()}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ category, percentage }) => `${category}: ${percentage}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="count"
                    >
                      {getReadingsByCategory().map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* Recommendations */}
          <Grid item xs={12} md={8}>
            <Card>
              <CardHeader 
                title="Recommendations"
                avatar={<Insights color="primary" />}
              />
              <CardContent>
                {analytics.recommendations && analytics.recommendations.length > 0 ? (
                  <List>
                    {analytics.recommendations.map((recommendation, index) => (
                      <React.Fragment key={index}>
                        <ListItem>
                          <ListItemIcon>
                            <CheckCircle color="success" />
                          </ListItemIcon>
                          <ListItemText primary={recommendation} />
                        </ListItem>
                        {index < analytics.recommendations.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body1" color="text.secondary">
                    Keep recording your blood pressure regularly to receive personalized recommendations.
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Quick Stats */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardHeader 
                title="Quick Stats"
                avatar={<Timeline color="primary" />}
              />
              <CardContent>
                <Box mb={2}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Highest Reading
                  </Typography>
                  <Typography variant="h6">
                    {Math.max(...readings.map(r => r.systolic))}/{Math.max(...readings.map(r => r.diastolic))}
                  </Typography>
                </Box>
                
                <Box mb={2}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Lowest Reading
                  </Typography>
                  <Typography variant="h6">
                    {Math.min(...readings.map(r => r.systolic))}/{Math.min(...readings.map(r => r.diastolic))}
                  </Typography>
                </Box>
                
                <Box mb={2}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Most Recent
                  </Typography>
                  <Typography variant="h6">
                    {readings[0]?.systolic || 0}/{readings[0]?.diastolic || 0}
                  </Typography>
                </Box>
                
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Recording Frequency
                  </Typography>
                  <Typography variant="body1">
                    {readings.length > 0 ? `${Math.round(readings.length / (period === 'week' ? 7 : period === 'month' ? 30 : period === 'quarter' ? 90 : 365))} readings per day` : 'No data'}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Patterns */}
          {analytics.patterns && (
            <Grid item xs={12}>
              <Card>
                <CardHeader 
                  title="Patterns & Insights"
                  avatar={<Assessment color="primary" />}
                />
                <CardContent>
                  <Typography variant="body1" paragraph>
                    Based on your blood pressure readings, we've identified some patterns that may help you better understand your cardiovascular health.
                  </Typography>
                  
                  {analytics.patterns.timeOfDay && (
                    <Box mb={2}>
                      <Typography variant="h6" gutterBottom>
                        Time of Day Patterns
                      </Typography>
                      <Typography variant="body2">
                        Your blood pressure tends to be {analytics.patterns.timeOfDay.trend} during {analytics.patterns.timeOfDay.bestTime}.
                      </Typography>
                    </Box>
                  )}
                  
                  {analytics.patterns.weekly && (
                    <Box mb={2}>
                      <Typography variant="h6" gutterBottom>
                        Weekly Patterns
                      </Typography>
                      <Typography variant="body2">
                        Your readings show a {analytics.patterns.weekly.trend} pattern throughout the week.
                      </Typography>
                    </Box>
                  )}
                  
                  {analytics.patterns.consistency && (
                    <Box>
                      <Typography variant="h6" gutterBottom>
                        Consistency Score
                      </Typography>
                      <Box display="flex" alignItems="center">
                        <Box width="100%" mr={1}>
                          <LinearProgress 
                            variant="determinate" 
                            value={analytics.patterns.consistency.score} 
                            sx={{ height: 10, borderRadius: 5 }}
                          />
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          {analytics.patterns.consistency.score}%
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        {analytics.patterns.consistency.description}
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      )}
    </Container>
  );
};

export default AnalyticsPage; 